/**
 * Ready Watch Configurator Logic v7 (Robust Matching)
 * Handles fuzzy variant matching and surgical UI state management.
 */
(function() {
  const R2_BASE = "https://calibre-worker.calibre.workers.dev";

  function initReadyConfig() {
    const root = /** @type {HTMLElement | null} */ (document.getElementById('ready-watch-config-root'));
    if (!root) return;

    if (root.dataset.init === 'true') return;
    root.dataset.init = 'true';

    // Strictly hide S Logo button to bypass any Liquid caching or CLI syncing delays
    root.querySelectorAll('.option[data-type="logo"]').forEach(btn => {
       if (btn.dataset.id === 'logo__slogo' || btn.textContent.toLowerCase().includes('s logo')) {
          btn.style.setProperty('display', 'none', 'important');
       }
    });

    /** @type {any[]} */
    const variants = JSON.parse(root.dataset.variants || '[]');
    const logoUploadBox = document.getElementById('custom-logo-upload');
    const totalPriceEl = document.getElementById('ready-total-price');
    if (totalPriceEl) {
      // Pre-set cents for first-load matching
      const initialPrice = root.dataset.initialPriceCents || "0";
      totalPriceEl.dataset.priceCents = initialPrice;
    }
    const deliveryEl = document.getElementById('ready-delivery-notice');
    
    const currencySymbol = root.dataset.currencySymbol || "₹";
    const currencyCode = (root.dataset.currencyCode || "INR").toLowerCase();
    const locale = root.dataset.locale || "en-IN";

    // --- State Management ---
    const activeLogoNode = root.querySelector('.option[data-type="logo"].active');
    const activeColorwayNode = root.querySelector('.option[data-type="colorway"].active');
    const activeSizeNode = root.querySelector('.option[data-type="case-size"].active') || root.querySelector('.option[data-type="case_size"].active');
    
    const caseBackInput = /** @type {HTMLInputElement | null} */ (document.getElementById('ready-case-back-input'));
    const state = {
      colorway: activeColorwayNode instanceof HTMLElement ? activeColorwayNode.dataset.id || "" : "",
      logo: activeLogoNode instanceof HTMLElement ? activeLogoNode.dataset.id || "logo__none" : "logo__none",
      metal_logo_finish: "Silver",
      case_size: activeSizeNode instanceof HTMLElement ? activeSizeNode.dataset.id || "" : "",
      case_back: caseBackInput ? caseBackInput.value : "Solid Case Back",
      logo_url: ""
    };

    /**
     * --- Surgical UI Hiding ---
     * Targets specific theme blocks to prevent duplication.
     */
    function hideThemeRedundancy() {
        const selectors = [
           '.product-information > .product-title-block',
           '.product-information > .product-price-block',
           '.product-information > .product-description',
           '.product-information .product__title',
           '.product-information h1',
           '.product-information h2',
           '.product-information h3',
           '.product-information h1.product__title',
           '.product-information .product__description',
           '.product-information .product-description',
           '.product-information .description',
           '.product-information .product__text',
           '.product-information [data-description]',
           '.product-information .price',
           '.product-information .price-block',
           '.product-information .share-button'
        ];
        
        const prodTitle = document.title.split('|')[0].trim();
        const prodSnippet = root.dataset.descriptionSnippet || "";

        selectors.forEach(sel => {
           document.querySelectorAll(sel).forEach(el => {
              if (el instanceof HTMLElement && !el.closest('#ready-watch-config-root')) {
                 el.style.setProperty('display', 'none', 'important');
                 el.style.setProperty('visibility', 'hidden', 'important');
                 el.style.setProperty('height', '0', 'important');
                 el.style.setProperty('margin', '0', 'important');
                 el.style.setProperty('padding', '0', 'important');
              }
           });
        });

        // Breadcrumb and small metadata check - EXEMPT config title
        document.querySelectorAll('h1, h2, h3, .price, .product-title, .product__description, .product-description, .description, [data-description], .product__text').forEach(el => {
           if (el instanceof HTMLElement && !el.closest('#ready-watch-config-root') && !el.classList.contains('config-title') && !el.classList.contains('configurator-block-title')) {
              const text = el.innerText;
              const matchesTitle = prodTitle && (text.includes(prodTitle) || text === prodTitle);
              const matchesDesc = prodSnippet && text.includes(prodSnippet);

              if (matchesTitle || matchesDesc) {
                 el.style.setProperty('display', 'none', 'important');
              }
           }
        });
    }

    /**
     * --- Intercept Theme Logic ---
     */
    const variantPicker = /** @type {HTMLElement | null} */ (document.querySelector('variant-picker'));
    if (variantPicker && !variantPicker.dataset.intercepted) {
       variantPicker.dataset.intercepted = "true";
       // @ts-ignore
       if (variantPicker.fetchUpdatedSection) {
          // @ts-ignore
          variantPicker.fetchUpdatedSection = function() {
             console.log("[Calibre] Morph update intercepted.");
          };
       }
    }

    /**
     * @param {string} namePart
     */
    function getThemeOptionRadios(namePart) {
      if (!variantPicker) return [];
      const fieldsets = variantPicker.querySelectorAll('fieldset');
      for (const fs of Array.from(fieldsets)) {
        const legend = fs.querySelector('legend');
        const legendText = legend ? legend.textContent?.toLowerCase() : '';
        if (legendText && (legendText.includes(namePart.toLowerCase()) || namePart.toLowerCase().includes(legendText))) {
          return /** @type {HTMLInputElement[]} */ (Array.from(fs.querySelectorAll('input[type="radio"]')));
        }
      }
      return [];
    }

    /**
     * Smart string matching for variants (e.g. "No Logo" vs "logo__none")
     */
    function fuzzyMatch(shopifyValue, ourId) {
       const s = shopifyValue.toLowerCase().replace(/[^a-z0-9]/g, '');
       const o = ourId.toLowerCase().replace('logo__', '').replace(/[^a-z0-9]/g, '');
       
       if (s === o) return true;
       if (o === 'none' && (s === 'nologo' || s === 'no')) return true;
       if (s.includes(o) || o.includes(s)) return true;
       return false;
    }

    function syncWithTheme(namePart, value) {
       const radios = getThemeOptionRadios(namePart);
       const target = radios.find(r => fuzzyMatch(r.value, value));
       
       if (target) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
       }
    }

    function performFullSync() {
       syncWithTheme('logo', state.logo);
       syncWithTheme('size', state.case_size);
       const caseBackInput = /** @type {HTMLInputElement | null} */ (document.getElementById('ready-case-back-input'));
       if (caseBackInput) {
          caseBackInput.value = state.case_back;
          caseBackInput.dispatchEvent(new Event('change', { bubbles: true }));
       }
    }

    /**
     * @param {number} cents
     */
    function formatPrice(cents) {
      const value = Math.round(cents / 100);
      return `${currencySymbol}${value.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}`;
    }

    let readyPriceAnimId = null;

    /**
     * @param {any} cents
     */
    function animatePrice(cents) {
       const el = totalPriceEl;
       if (!el) return;

       if (readyPriceAnimId) {
          cancelAnimationFrame(readyPriceAnimId);
          readyPriceAnimId = null;
       }

       const start = parseInt(el.dataset.priceCents || "0");
       const end = cents;

       if (start === end && start !== 0) {
          el.innerText = formatPrice(end);
          el.dataset.priceCents = end.toString();
          return;
       }

       const duration = 400;
       const startTime = performance.now();

       /**
        * @param {number} now
        */
       function frame(now) {
          const progress = Math.min((now - startTime) / duration, 1);
          const current = Math.floor(start + (end - start) * progress);
          if (el) {
             el.innerText = formatPrice(current);
             el.dataset.priceCents = current.toString();
          }

          if (progress < 1) {
             readyPriceAnimId = requestAnimationFrame(frame);
          } else if (el) {
             readyPriceAnimId = null;
             el.dataset.priceCents = end.toString();
             el.classList.add("bump");
             setTimeout(() => el.classList.remove("bump"), 200);
          }
       }
       readyPriceAnimId = requestAnimationFrame(frame);
    }

    let imageLock = { url: null, observer: null };

    /**
     * Surgically swaps the main product gallery image.
     * @param {string} url 
     */
    function swapMainImage(url) {
       if (!url) return;
       console.log('[Configurator] Swapping main image to:', url);
       imageLock.url = url;

       // Update the hidden input for Cart/Admin
       const hiddenImgInput = /** @type {HTMLInputElement | null} */ (document.getElementById('ready-colorway-image-input'));
       if (hiddenImgInput) {
          hiddenImgInput.value = url;
          console.log('[Configurator] Cart Property "_calibre_render_url" updated.');
       }

       // Target ONLY the absolute first primary product image(s)
       const gallerySelectors = [
           'media-gallery [ref="media[]"]:first-child img',
           '.media-gallery__grid li:first-child img',
           '.product-media-container--image:first-of-type img',
           '.product__media-item:first-child img',
           '.product-media__image:first-of-type'
       ];

       const updateImg = (img) => {
          if (!img) return;
          console.log('[Configurator] Locking attributes for:', img);
          
          if (img._lockObserver) {
             img._lockObserver.disconnect();
             img._lockObserver = null;
          }

          // Keep track of target to prevent race conditions
          img.dataset.targetUrl = url;

          // Preload the new watch configuration image
          const tempImg = new Image();
          tempImg.onload = () => {
             if (img.dataset.targetUrl !== url) return;

             // Ensure the parent container supports absolute positioning overlay
             const parent = img.parentElement;
             if (parent) {
                const originalPosition = window.getComputedStyle(parent).position;
                if (originalPosition === 'static') {
                   parent.style.position = 'relative';
                }
             }

             // 1. Create overlay clone of the old image
             const clone = img.cloneNode(true);
             clone.removeAttribute("id");
             clone.style.position = "absolute";
             clone.style.top = "0";
             clone.style.left = "0";
             clone.style.width = "100%";
             clone.style.height = "100%";
             clone.style.pointerEvents = "none";
             clone.style.transition = "opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)";
             clone.style.opacity = "1";
             clone.style.zIndex = "10";

             // Insert clone right after the original image
             img.parentNode.insertBefore(clone, img.nextSibling);

             // 2. Instantly update original image underneath
             img.src = url;
             img.srcset = url;
             img.setAttribute('data-src', url);
             img.setAttribute('data-srcset', url);
             img.setAttribute('data-max-resolution', url);
             img.setAttribute('data_max_resolution', url);
             img.style.opacity = '1';

             // 3. Fade out clone
             requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                   clone.style.opacity = "0";
                });
             });

             // 4. Remove clone when done
             setTimeout(() => {
                clone.remove();
             }, 550);

             // Reconnect mutation observer after change to enforce lock
             if (img._lockObserver) img._lockObserver.disconnect();
             img._lockObserver = new MutationObserver((mutations) => {
                 if (imageLock.url && (img.src !== imageLock.url || img.getAttribute('data_max_resolution') !== imageLock.url)) {
                     console.log('[Configurator] Mutation detected, re-locking image URL.');
                     img.src = imageLock.url;
                     img.srcset = imageLock.url;
                     img.setAttribute('data_max_resolution', imageLock.url);
                     img.setAttribute('data-max-resolution', imageLock.url);
                 }
             });
             img._lockObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset', 'data_max_resolution', 'data-max-resolution'] });
          };

          tempImg.onerror = () => {
             if (img.dataset.targetUrl !== url) return;
             img.src = url;
             img.style.opacity = '1';
          };
          tempImg.src = url;
       };

       gallerySelectors.forEach(selector => {
           const images = document.querySelectorAll(selector);
           if (images.length > 0) updateImg(images[0]);
       });

       // Also update the zoom gallery images (Standard Horizon/Dawn classes)
       const zoomSelectors = [
           '.dialog-zoomed-gallery li:first-child img',
           'zoom-dialog [ref="media[]"]:first-child img',
           '.product-media__drag-zoom-wrapper img'
       ];
       
       zoomSelectors.forEach(selector => {
           const zImgs = document.querySelectorAll(selector);
           if (zImgs.length > 0) updateImg(zImgs[0]);
       });
    }

    function updateUI() {
       // Find current variant based on fuzzy matching logic
       const currentVariant = variants.find(v => {
          const vOptions = Array.isArray(v.options) ? v.options : [];
          const matchSize = vOptions.some(o => state.case_size && o.toLowerCase().includes(state.case_size.toLowerCase()));
          const matchLogo = vOptions.some(o => fuzzyMatch(o, state.logo));
          return matchLogo && matchSize;
       }) || variants.find(v => {
          // Fallback: match just logo if size match fails
          const vOptions = Array.isArray(v.options) ? v.options : [];
          return vOptions.some(o => fuzzyMatch(o, state.logo));
       }) || variants[0];

       if (totalPriceEl && currentVariant) {
          animatePrice(currentVariant.price);
          console.log("[Calibre] Target Price:", currentVariant.price, "Variant:", currentVariant.id);
       }

       if (currentVariant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', currentVariant.id.toString());
          window.history.replaceState({ path: url.href }, '', url.href);

          // IMPORTANT: Update the theme's hidden variant ID input
          // This ensures the correct variant is added to the cart.
          const variantIdInput = /** @type {HTMLInputElement | null} */ (document.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'));
          if (variantIdInput) {
             variantIdInput.value = currentVariant.id.toString();
             // Trigger change event just in case other things are listening
             variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));

             // --- SURGICAL PROPERTY INJECTION ---
             // We must ensure all Line Item Properties are inside the ACTUAL product form
             // otherwise Shopify Checkout (especially Buy Now) will ignore them
             const targetForm = variantIdInput.closest('form');
             if (targetForm) {
                /** 
                 * @param {string} name 
                 * @param {string} val 
                 */
                const syncProperty = (name, val) => {
                   let input = /** @type {HTMLInputElement | null} */ (targetForm.querySelector(`input[name="properties[${name}]"]`));
                   if (!input) {
                      input = document.createElement('input');
                      input.type = 'hidden';
                      input.name = `properties[${name}]`;
                      targetForm.appendChild(input);
                   }
                   if (input.value !== val) {
                      input.value = val;
                      input.dispatchEvent(new Event('change', { bubbles: true }));
                   }
                };

                // Store for swapMainImage to optionally use
                window._readySyncProp = syncProperty;

                const logoLabel = state.logo === "logo__none" ? "No Logo" : 
                                  state.logo === "logo__slogo" ? "S Logo" : 
                                  state.logo === "logo__printed" ? "Custom Printed Logo" : 
                                  state.logo === "logo__metal" ? "Custom Metal Logo" : state.logo;

                // ONLY SYNC PROPERTIES IF THE UI SECTIONS ACTUALLY EXIST ON THIS PRODUCT
                 if (root && root.querySelector('[data-section="colorway"]')) {
                    syncProperty('Colorway', state.colorway || "N/A");
                    // Sync the image URL as well
                    const hiddenImg = /** @type {HTMLInputElement | null} */ (document.getElementById('ready-colorway-image-input'));
                    if (hiddenImg) syncProperty('_calibre_render_url', hiddenImg.value);
                 }
                 if (root && root.querySelector('[data-section="case-size"]')) {
                    syncProperty('Case Size', state.case_size || "N/A");
                 }
                 if (root && root.querySelector('[data-section="case_back"]')) {
                    syncProperty('Case Back', state.case_back || (caseBackInput ? caseBackInput.value : "Solid Case Back"));
                 }
                 if (root && root.querySelector('[data-section="logo"]')) {
                    syncProperty('Logo Option', logoLabel);
                    syncProperty('_Logo URL', state.logo_url || "None Provided");
                    if (state.logo.toLowerCase().includes('metal')) {
                       syncProperty('Metal Logo Finish', state.metal_logo_finish || "Silver");
                    } else {
                       const metalInput = targetForm.querySelector('input[name="properties[Metal Logo Finish]"]');
                       if (metalInput) metalInput.remove();
                    }
                 }
             }
          }
       }

       // Button validation & Logo Upload Box visibility
       const isCustomLogo = state.logo.toLowerCase().includes('printed') || state.logo.toLowerCase().includes('metal');
       const isMissingLogo = isCustomLogo && !state.logo_url;
       const isMetalLogo = state.logo.toLowerCase().includes('metal');
       
       if (logoUploadBox) {
         logoUploadBox.classList.toggle('is-visible', isCustomLogo);
         const metalFinishBox = logoUploadBox.querySelector('#metal-finish-selector') || document.getElementById('metal-finish-selector');
          if (metalFinishBox) {
            metalFinishBox.classList.toggle('is-visible', isMetalLogo);
            metalFinishBox.classList.remove('hidden');
          }
       }

       const atcButtons = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('product-form-component button[name="add"], product-form-component .shopify-payment-button__button, product-form button[name="add"], sticky-add-to-cart [ref="addToCartButton"]'));
       
       atcButtons.forEach(btn => {
          /** @param {string} t */
          const setMsg = (t) => {
             const s = btn.querySelector('.add-to-cart-text__content span span') || btn.querySelector('.add-to-cart-text__content span') || btn.querySelector('.add-to-cart-text') || btn;
             if (s && s !== btn) { 
                if (/** @type {HTMLElement} */ (s).innerText !== t) /** @type {HTMLElement} */ (s).innerText = t; 
             } else { 
                if (btn.innerText !== t) btn.innerText = t; 
             }
          };

          if (isMissingLogo) {
             btn.setAttribute('disabled', 'disabled');
             btn.style.opacity = '0.5';
             setMsg("UPLOAD LOGO FIRST");
          } else {
             btn.removeAttribute('disabled');
             btn.style.opacity = '1';
             if (btn.classList.contains('shopify-payment-button__button')) {
                setMsg("BUY IT NOW");
             } else if (btn.closest('sticky-add-to-cart')) {
                setMsg("ADD TO CART");
             } else {
                setMsg("ADD TO CART");
             }
          }
       });


       if (deliveryEl) {
         deliveryEl.innerText = "Delivery within 3-4 weeks";
       }

       // Update Sticky ATC Bar if it exists
       const stickyBar = /** @type {HTMLElement | null} */ (document.querySelector('sticky-add-to-cart'));
       if (stickyBar && currentVariant) {
          const sPrice = /** @type {HTMLElement | null} */ (stickyBar.querySelector('.sticky-add-to-cart__price .price') || stickyBar.querySelector('.sticky-add-to-cart__price'));
          if (sPrice) sPrice.innerText = formatPrice(currentVariant.price);
          
          const sVariant = /** @type {HTMLElement | null} */ (stickyBar.querySelector('.sticky-add-to-cart__variant'));
          if (sVariant) {
             const logoLabel = state.logo === "logo__none" ? "No Logo" : 
                               state.logo === "logo__slogo" ? "S Logo" : 
                               state.logo === "logo__printed" ? "Printed Logo" : 
                               state.logo === "logo__metal" ? "Metal Logo" : state.logo;
             sVariant.innerText = `${state.case_size || ""} / ${logoLabel}`;
          }

          // Also update the variant ID on the sticky component just in case
          stickyBar.dataset.currentVariantId = currentVariant.id.toString();
          stickyBar.dataset.variantAvailable = (currentVariant.available !== false).toString();
       }

        // Update logo option price note labels (+/- cost relative to current selection)
        if (currentVariant) {
           const basePrice = currentVariant.price;
           root.querySelectorAll('[data-type="logo"]').forEach(btn => {
              const id = btn.dataset.id;
              if (!id) return;

              // Find variant corresponding to this logo option and the currently selected size
              const targetVariant = variants.find(v => {
                 const vOptions = Array.isArray(v.options) ? v.options : [];
                 const matchSize = vOptions.some(o => state.case_size && o.toLowerCase().includes(state.case_size.toLowerCase()));
                 const matchLogo = vOptions.some(o => fuzzyMatch(o, id));
                 return matchLogo && matchSize;
              }) || variants.find(v => {
                 const vOptions = Array.isArray(v.options) ? v.options : [];
                 return vOptions.some(o => fuzzyMatch(o, id));
              }) || variants[0];

              if (!targetVariant) return;
              const diff = targetVariant.price - basePrice;
              const label = btn.querySelector('.logo-price-note');
              if (label) {
                 if (diff === 0) {
                    label.textContent = "Included in Price";
                 } else if (diff > 0) {
                    label.textContent = `+ ${formatPrice(diff)}`;
                 } else {
                    label.textContent = `- ${formatPrice(Math.abs(diff))}`;
                 }
              }
           });
        }

       hideThemeRedundancy();
    }

    // Interactions
    const metalFinishContainer = root.querySelector('#metal-finish-selector') || document.querySelector('#metal-finish-selector');
    const metalFinishLabel = root.querySelector('#metal-finish-label') || document.querySelector('#metal-finish-label');
    const metalFinishInput = /** @type {HTMLInputElement | null} */ (root.querySelector('#metal-finish-input') || document.querySelector('#metal-finish-input'));

    if (metalFinishContainer) {
      metalFinishContainer.querySelectorAll('.metal-swatch').forEach(node => {
        const swatch = /** @type {HTMLElement} */ (node);
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          metalFinishContainer.querySelectorAll('.metal-swatch').forEach(s => s.classList.remove('active'));
          swatch.classList.add('active');
          const label = swatch.dataset.label || "Silver";
          state.metal_logo_finish = label;
          if (metalFinishLabel) metalFinishLabel.textContent = label;
          if (metalFinishInput) {
            metalFinishInput.value = label;
            metalFinishInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          updateUI();
        });
      });
    }

    root.querySelectorAll('.option.text-option, .option.thumb-option').forEach(node => {
      const opt = /** @type {HTMLElement} */ (node);
      opt.addEventListener('click', (e) => {
        e.preventDefault();
        const type = opt.dataset.type;
        const id = opt.dataset.id;
        if (!type || !id) return;

        const parent = opt.closest('.options');
        if (parent) parent.querySelectorAll('.option').forEach(o => o.classList.remove('active'));
        opt.classList.add('active');

        if (type === 'colorway') {
           state.colorway = id;
           const fullImg = opt.getAttribute('data-full-image');
           if (fullImg) swapMainImage(fullImg);
        }
        if (type === 'logo') state.logo = id;
        if (type === 'case_size' || type === 'case-size') state.case_size = id;
        if (type === 'case_back') state.case_back = opt.dataset.value || opt.innerText.trim();
        
        performFullSync();
        updateUI();
      });
    });

    // Accordion Toggle
    root.querySelectorAll('.config-section-toggle').forEach(node => {
      node.addEventListener('click', (e) => {
        const section = node.closest('.config-section');
        if (!section) return;
        const isOpen = section.classList.contains('open');
        root.querySelectorAll('.config-section').forEach(s => s.classList.remove('open'));
        if (!isOpen) section.classList.add('open');
      });
    });

    // File Upload
    const fileInput = /** @type {HTMLInputElement | null} */ (document.getElementById('logo-file'));
    const statusEl = document.getElementById('logo-upload-status');
    const hiddenLogoInput = /** @type {HTMLInputElement | null} */ (document.getElementById('ready-logo-url-input'));
    const previewEl = document.getElementById('logo-preview-wrapper');
    const previewImg = /** @type {HTMLImageElement | null} */ (document.getElementById('logo-preview-image'));

    if (fileInput) {
      fileInput.addEventListener('change', async function() {
        const file = this.files ? this.files[0] : null;
        if (!file || file.type !== "image/png") return;
        if (statusEl) statusEl.innerText = "Uploading...";
        
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = /** @type {string} */ (e.target?.result);
          const img = new Image();
          img.onload = async () => {
             if (img.width < 400 || img.height < 400) {
               alert("Min 400x400 required.");
               return;
             }
             try {
                const res = await fetch(`${R2_BASE}/upload`, {
                   method: "POST", 
                   headers: { "Content-Type": "application/json" },
                   body: JSON.stringify({ fileName: `logos/ready-${Date.now()}.png`, fileData: base64 })
                });
                const result = await res.json();
                if (result.success) {
                   state.logo_url = result.url;
                   if (hiddenLogoInput) { 
                      hiddenLogoInput.value = result.url; 
                      hiddenLogoInput.dispatchEvent(new Event('change', { bubbles: true })); 
                   }
                   if (previewEl && previewImg) { 
                      previewImg.src = base64; 
                      previewEl.classList.remove('hidden'); 
                   }
                   if (statusEl) statusEl.innerText = "✓ Logo Ready";
                   updateUI();
                }
             } catch (err) { 
                console.error("Upload error:", err);
                alert("Upload failed"); 
             }
          };
          img.src = base64;
        };
        reader.readAsDataURL(file);
      });
    }

    performFullSync();
    updateUI();
    
    // Auto-swap main image for the first colorway on load
    const firstColorBtn = /** @type {HTMLElement | null} */ (root.querySelector('.option[data-type="colorway"].active'));
    if (firstColorBtn) {
        const initialImg = firstColorBtn.getAttribute('data-full-image');
        if (initialImg) swapMainImage(initialImg);
    }

    // --- Mobile Tabs Integration ---
    let currentMode = null;

    function initMobileTabs() {
      const tabs = root.querySelectorAll(".mobile-tab");
      const sections = root.querySelectorAll(".config-section");

      function activateSection(target) {
        sections.forEach(section => {
          if (section.dataset.section === target) {
            section.classList.remove("mobile-hidden");
            section.classList.remove("collapsed");
            section.classList.add("open");

            const content = section.querySelector(".config-section-content");
            if (content) content.style.maxHeight = "none";
          } else {
            section.classList.add("mobile-hidden");
            section.classList.remove("open");
          }
        });

        tabs.forEach(tab => {
          tab.classList.toggle("active", tab.dataset.target === target);
        });
      }

      if (tabs.length) {
        // Find if there's an already active tab or default to first
        const activeTab = Array.from(tabs).find(t => t.classList.contains('active')) || tabs[0];
        activateSection(activeTab.dataset.target);
      }

      tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          activateSection(tab.dataset.target);
        });
      });

      const scrollContainer = root.querySelector('#mobile-tabs-scroll');
      const leftBtn = root.querySelector('#mobile-tabs-left');
      const rightBtn = root.querySelector('#mobile-tabs-right');

      if (scrollContainer && leftBtn && rightBtn) {
        const updateArrows = () => {
          if (scrollContainer.scrollLeft > 5) leftBtn.classList.remove('hidden');
          else leftBtn.classList.add('hidden');

          if (scrollContainer.scrollLeft < scrollContainer.scrollWidth - scrollContainer.clientWidth - 5) rightBtn.classList.remove('hidden');
          else rightBtn.classList.add('hidden');
        };

        if (!scrollContainer.dataset.arrowsInit) {
          scrollContainer.addEventListener('scroll', updateArrows);
          window.addEventListener('resize', updateArrows);
          scrollContainer.dataset.arrowsInit = 'true';
        }

        setTimeout(updateArrows, 100);

        leftBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollContainer.scrollBy({ left: -150, behavior: 'smooth' });
        });
        rightBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollContainer.scrollBy({ left: 150, behavior: 'smooth' });
        });
      }
    }

    function initDesktopAccordion() {
      const sections = root.querySelectorAll(".config-section");
      sections.forEach(section => {
        section.classList.remove("mobile-hidden");
        // Keep accordion functionality as is for desktop
      });
    }

    function handleResponsiveLayout() {
      const isMobile = window.innerWidth <= 900;
      const newMode = isMobile ? "mobile" : "desktop";

      if (newMode === currentMode) return;
      currentMode = newMode;

      if (newMode === "mobile") {
        initMobileTabs();
      } else {
        initDesktopAccordion();
      }
    }

    handleResponsiveLayout();
    window.addEventListener("resize", handleResponsiveLayout);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReadyConfig);
  } else {
    initReadyConfig();
  }
})();
